import { Switch, Route, useLocation, useParams, Redirect } from "wouter";
import { useEffect, lazy, Suspense, useState } from "react";

// Baked in at build time from VITE_APP_PROFILE — zero API latency, no flash.
// Used to hard-gate routes that should never be reachable on the agency site.
const IS_AGENCY_BUILD = import.meta.env.VITE_APP_PROFILE === "agency";
import { useFeatureFlags } from "@/lib/FeatureFlagContext";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary, PublicPageErrorBoundary } from "@/components/ErrorBoundary";
import { SiteConfigProvider } from "@/lib/SiteConfigContext";
import { FeatureFlagProvider } from "@/lib/FeatureFlagContext";
import { ModalProvider, useModalContext } from "@/contexts/modal-context";
import { Layout } from "@/components/Layout";
import { StudioTrainingBanner } from "@/components/StudioTrainingBanner";
import { initGA, initMetaPixel, initMetaPixelWithId } from "@/lib/analytics";
import { getNguTrackingConfig } from "@/lib/nguTrackingConfig";
import { initSentry, captureAuthFailure } from "@/lib/sentry";
// Sentry is lazy-loaded — no static import of @sentry/react in the main bundle
import { registerServiceWorker } from "@/lib/sw-register";
import { initAttribution } from "@/hooks/useAttribution";
import { useAnalytics } from "@/hooks/use-analytics";
import { getStoredConsent } from "@/components/CookieConsent";
import { BannerSequence } from "@/components/BannerSequence";
import { fetchArtistsFromApi } from "@/lib/artists";
import { apiFetch } from "@/lib/apiFetch";
import { systemFlagsContract } from "@shared/contracts";
import { NguThemeProvider, NguThemeToggle } from "@/components/NguThemeProvider";
import { ClarityProvider } from "@/components/ClarityProvider";
import AdminColorSchemeWidget from "@/components/AdminColorSchemeWidget";
// DevFlagPanel only loads in dev — prod gets a no-op stub so no flag metadata ships
const DevFlagPanel = lazy((() =>
  import.meta.env.DEV
    ? import("@/components/DevFlagPanel").then(m => ({ default: m.DevFlagPanel }))
    : Promise.resolve({ default: () => null })
) as () => Promise<{ default: React.ComponentType<any> }>);
const AdminCommandPalette = lazy(() =>
  import("@/components/AdminCommandPalette").then(m => ({ default: m.AdminCommandPalette }))
);
import { useStudioConfig } from "@/hooks/use-studio-config";
import Home from "@/pages/Home";
import AgencyHome from "@/pages/AgencyHome";
import NotFound from "@/pages/not-found";

const Artist = lazy(() => import("@/pages/Artist"));
const Events = lazy(() => import("@/pages/Events"));
const About = lazy(() => import("@/pages/About"));
const FlashDesigns = lazy(() => import("@/pages/FlashDesigns"));
const Shop = lazy(() => import("@/pages/Shop"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const Accessibility = lazy(() => import("@/pages/Accessibility"));
const Contact = lazy(() => import("@/pages/Contact"));
const HealingTracker = lazy(() => import("@/pages/HealingTracker"));
const Aftercare = lazy(() => import("@/pages/Aftercare"));
const PiercingAftercare = lazy(() => import("@/pages/PiercingAftercare"));
const NumbingCreamInfo = lazy(() => import("@/pages/NumbingCreamInfo"));
const StyleQuiz = lazy(() => import("@/pages/StyleQuiz"));
const MailingList = lazy(() => import("@/pages/MailingList"));
const BookingCalendar = lazy(() => import("@/pages/BookingCalendar"));
const CostEstimator = lazy(() => import("@/pages/CostEstimator"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const RescheduleBooking = lazy(() => import("@/pages/RescheduleBooking"));
const WaitlistClaim = lazy(() => import("@/pages/WaitlistClaim"));
const EditTattooDetails = lazy(() => import("@/pages/EditTattooDetails"));
const NpsRating = lazy(() => import("@/pages/NpsRating"));
const BookingMessages = lazy(() => import("@/pages/BookingMessages"));
const AdminAction = lazy(() => import("@/pages/AdminAction"));
const AdminChat = lazy(() => import("@/pages/AdminChat"));
const ArtistPortal = lazy(() => import("@/pages/ArtistPortal"));
const DesignReview = lazy(() => import("@/pages/DesignReview"));
const ManageAvailability = lazy(() => import("@/pages/ManageAvailability"));
const EmailSettings = lazy(() => import("@/pages/EmailSettings"));
const EstimateModel = lazy(() => import("@/pages/EstimateModel"));
const WalkinBooking = lazy(() => import("@/pages/WalkinBooking"));
const EmbedBooking = lazy(() => import("@/pages/EmbedBooking"));
const ManageArtists = lazy(() => import("@/pages/ManageArtists"));
const ManageStaff = lazy(() => import("@/pages/ManageStaff"));
const ReceptionistDashboard = lazy(() => import("@/pages/ReceptionistDashboard"));
const ReceptionistPortal = lazy(() => import("@/pages/ReceptionistPortal"));
const AgencyAssistantPortal = lazy(() => import("@/pages/AgencyAssistantPortal"));
const AgencyAssistantOnboarding = lazy(() => import("@/pages/AgencyAssistantOnboarding"));
const AgencyPortalTokens = lazy(() => import("@/pages/AgencyPortalTokens"));
const ManageEvents = lazy(() => import("@/pages/ManageEvents"));
const NetGlowUp = lazy(() => import("@/pages/NetGlowUp"));
const NetGlowUpCalculatorPage = lazy(() => import("@/pages/NetGlowUp").then(m => ({ default: m.NetGlowUpCalculatorPage })));
const NetGlowUpProfile = lazy(() => import("@/pages/NetGlowUpProfile"));
const NetGlowUpReport = lazy(() => import("@/pages/NetGlowUpReport"));
const NetGlowUpAdminPage = lazy(() => import("@/pages/NetGlowUpAdminPage"));
const OnboardingSurvey = lazy(() => import("@/pages/OnboardingSurvey"));
const AdminOnboardingPanel = lazy(() => import("@/pages/AdminOnboardingPanel"));
const NetGlowUpFunnelPage = lazy(() => import("@/pages/NetGlowUpFunnelPage"));
const NetGlowUpHubPage = lazy(() => import("@/pages/NetGlowUpHubPage"));
const NguLeadMagnetHubPage = lazy(() => import("@/pages/NguLeadMagnetHubPage"));
const NetGlowUpHubCampaignSetup = lazy(() => import("@/pages/NetGlowUpHubCampaignSetup"));
const NetGlowUpHubSalesTokens = lazy(() => import("@/pages/NetGlowUpHubSalesTokens"));
const NetGlowUpHubAnalytics = lazy(() => import("@/pages/NetGlowUpHubAnalytics"));
const NetGlowUpHubDripSequence = lazy(() => import("@/pages/NetGlowUpHubDripSequence"));
const NetGlowUpAdsPage = lazy(() => import("@/pages/NetGlowUpAdsPage"));
const NetGlowUpAdsIntegrationPage = lazy(() => import("@/pages/NetGlowUpAdsIntegrationPage"));
const NetGlowUpMarketingDashboard = lazy(() => import("@/pages/NetGlowUpMarketingDashboard"));
const NetGlowUpWhyEmail = lazy(() => import("@/pages/NetGlowUpWhyEmail"));
const NetGlowUpCompliancePage = lazy(() => import("@/pages/NetGlowUpCompliancePage"));
const NetGlowUpIntakePage = lazy(() => import("@/pages/NetGlowUpIntakePage"));
const NguBookPage = lazy(() => import("@/pages/NguBookPage"));
const NetGlowUpSurveyResults = lazy(() => import("@/pages/NetGlowUpSurveyResults"));
const NetGlowUpPainSurveyPage = lazy(() => import("@/pages/NetGlowUpPainSurveyPage"));
const NetGlowUpPainSurveyResultsPage = lazy(() => import("@/pages/NetGlowUpPainSurveyResultsPage"));
const NetGlowUpArtistSurveyResultsPage = lazy(() => import("@/pages/NetGlowUpArtistSurveyResultsPage"));
const NetGlowUpWebinar = lazy(() => import("@/pages/NetGlowUpWebinar"));
const NetGlowUpWebinarHost = lazy(() => import("@/pages/NetGlowUpWebinarHost"));
const NetGlowUpCheckout = lazy(() => import("@/pages/NetGlowUpCheckout"));
const NetGlowUpCheckoutAgency = lazy(() => import("@/pages/NetGlowUpCheckoutAgency"));
const NetGlowUpStudioSignup = lazy(() => import("@/pages/NetGlowUpStudioSignup"));
const NetGlowUpSignupSuccess = lazy(() => import("@/pages/NetGlowUpSignupSuccess"));
const NetGlowUpComic = lazy(() => import("@/pages/NetGlowUpComic"));
const NetGlowUpDemo = lazy(() => import("@/pages/NetGlowUpDemo"));
const NetGlowUpStack = lazy(() => import("@/pages/NetGlowUpStack"));
const NetGlowUpStudiosPage = lazy(() => import("@/pages/NetGlowUpStudiosPage"));
const NetGlowUpConfigPage = lazy(() => import("@/pages/NetGlowUpConfigPage"));
const NgNewStudioWizard = lazy(() => import("@/pages/NgNewStudioWizard"));
const NgStudiosPage = lazy(() => import("@/pages/NgStudiosPage"));
const NgStudioPerformancePage = lazy(() => import("@/pages/NgStudioPerformancePage"));
const NgOpsPortalPage = lazy(() => import("@/pages/NgOpsPortalPage"));
// NetGlowUpPricingTiersPage only loads in dev — not yet promoted to production
const NetGlowUpPricingTiersPage = lazy((() =>
  import.meta.env.DEV ? import("@/pages/NetGlowUpPricingTiersPage") : Promise.resolve({ default: () => null })
) as () => Promise<{ default: React.ComponentType<any> }>);
const NetGlowUpSalesCoachPage = lazy(() => import("@/pages/NetGlowUpSalesCoachPage"));
const NetGlowUpOutreachPage = lazy(() => import("@/pages/NetGlowUpOutreachPage"));
const NguProspectListPage = lazy(() => import("@/pages/NguProspectListPage"));
const NetGlowUpPlaybookPage = lazy(() => import("@/pages/NetGlowUpPlaybookPage"));
const NetGlowUpPlaybookOutsourcePage = lazy(() => import("@/pages/NetGlowUpPlaybookOutsourcePage"));
const NetGlowUpQLAPage = lazy(() => import("@/pages/NetGlowUpQLAPage"));
const NetGlowUpAgenciesPage = lazy(() => import("@/pages/NetGlowUpAgenciesPage"));
const NetGlowUpMarketingPlanPage = lazy(() => import("@/pages/NetGlowUpMarketingPlanPage"));
const NetGlowUp24AssetsPage = lazy(() => import("@/pages/NetGlowUp24AssetsPage"));
const NetGlowUpMarketingChannelsPage = lazy(() => import("@/pages/NetGlowUpMarketingChannelsPage"));
const NetGlowUpIcpRegistryPage = lazy(() => import("@/pages/NetGlowUpIcpRegistryPage"));
const NetGlowUpRetentionPage = lazy(() => import("@/pages/NetGlowUpRetentionPage"));
const NetGlowUpCompetitorIntelPage = lazy(() => import("@/pages/NetGlowUpCompetitorIntelPage"));
const NetGlowUpSoftwareAsSciencePage = lazy(() => import("@/pages/NetGlowUpSoftwareAsSciencePage"));
const NetGlowUpHormoziOffersPage = lazy(() => import("@/pages/NetGlowUpHormoziOffersPage"));
const NetGlowUpHormoziLeadsPage = lazy(() => import("@/pages/NetGlowUpHormoziLeadsPage"));
const NetGlowUpHormoziMoneyModelsPage = lazy(() => import("@/pages/NetGlowUpHormoziMoneyModelsPage"));
const NetGlowUpContentHubPage = lazy(() => import("@/pages/NetGlowUpContentHubPage"));
const NetGlowUpCostPage = lazy(() => import("@/pages/NetGlowUpCostPage"));
const NetGlowUpMarketingChat = lazy(() => import("@/pages/NetGlowUpMarketingChat"));
const NetGlowUpClientUsagePage = lazy(() => import("@/pages/NetGlowUpClientUsagePage"));
const NguSignupsDashboard = lazy(() => import("@/pages/NguSignupsDashboard"));
const AdminNetGlowUpReportCard = lazy(() => import("@/pages/AdminNetGlowUpReportCard"));
const NetGlowUpComparisonPage = lazy(() => import("@/pages/NetGlowUpComparisonPage"));
const NetGlowUpFeaturesPage = lazy(() => import("@/pages/NetGlowUpFeaturesPage"));
const NetGlowUpFeatureDemoVideosPage = lazy(() => import("@/pages/NetGlowUpFeatureDemoVideosPage"));
const NetGlowUpFeaturesBookingPage = lazy(() => import("@/pages/NetGlowUpFeaturesBookingPage"));
const NetGlowUpFeaturesAccountingPage = lazy(() => import("@/pages/NetGlowUpFeaturesAccountingPage"));
const NetGlowUpFeaturesCommissionsPage = lazy(() => import("@/pages/NetGlowUpFeaturesCommissionsPage"));
const NetGlowUpFeaturesForecastingPage = lazy(() => import("@/pages/NetGlowUpFeaturesForecastingPage"));
const NetGlowUpFeaturesAutomationsPage = lazy(() => import("@/pages/NetGlowUpFeaturesAutomationsPage"));
const NetGlowUpFeaturesAnalyticsPage = lazy(() => import("@/pages/NetGlowUpFeaturesAnalyticsPage"));
const NetGlowUpFeaturesClientManagementPage = lazy(() => import("@/pages/NetGlowUpFeaturesClientManagementPage"));
const NetGlowUpOfferPage = lazy(() => import("@/pages/NetGlowUpOfferPage"));
const NguMagnetLandingPage = lazy(() => import("@/pages/NguMagnetLandingPage"));
const NguNoShowCalculatorPage = lazy(() => import("@/pages/NguNoShowCalculatorPage"));
const NguStudioAuditPage = lazy(() => import("@/pages/NguStudioAuditPage"));
const NguReminderTemplatesPage = lazy(() => import("@/pages/NguReminderTemplatesPage"));
const NguStackTeardownPage = lazy(() => import("@/pages/NguStackTeardownPage"));
const Ngu30DayRoadmapPage = lazy(() => import("@/pages/Ngu30DayRoadmapPage"));
const NguDmAuditPage = lazy(() => import("@/pages/NguDmAuditPage"));
const NguTattooPhotoGuidePage = lazy(() => import("@/pages/NguTattooPhotoGuidePage"));
const NguArtistOnboardingPage = lazy(() => import("@/pages/NguArtistOnboardingPage"));
const NguGbpGuidePage = lazy(() => import("@/pages/NguGbpGuidePage"));
const NguSocialStarterPage = lazy(() => import("@/pages/NguSocialStarterPage"));
const NguEmailListGuidePage = lazy(() => import("@/pages/NguEmailListGuidePage"));
const NguPromoCalendarPage = lazy(() => import("@/pages/NguPromoCalendarPage"));
const NguReviewPlaybookPage = lazy(() => import("@/pages/NguReviewPlaybookPage"));
const NguAftercareCardPage = lazy(() => import("@/pages/NguAftercareCardPage"));
const NguTouchUpPolicyPage = lazy(() => import("@/pages/NguTouchUpPolicyPage"));
const NguCaptionGuidePage = lazy(() => import("@/pages/NguCaptionGuidePage"));
const NguGetFeaturedPage = lazy(() => import("@/pages/NguGetFeaturedPage"));
const NguStyleGuidePage = lazy(() => import("@/pages/NguStyleGuidePage"));
const NguFlashDayPlaybookPage = lazy(() => import("@/pages/NguFlashDayPlaybookPage"));
const NguFlashDayChecklistPrintPage = lazy(() => import("@/pages/NguFlashDayChecklistPrintPage"));
const NguConsultationScriptPage = lazy(() => import("@/pages/NguConsultationScriptPage"));
const NguCommissionGuidePage = lazy(() => import("@/pages/NguCommissionGuidePage"));
const NguPortfolioCurationPage = lazy(() => import("@/pages/NguPortfolioCurationPage"));
const NguPricingGuidePage = lazy(() => import("@/pages/NguPricingGuidePage"));
const NguDepositPolicyPage = lazy(() => import("@/pages/NguDepositPolicyPage"));
const NguCityPage = lazy(() => import("@/pages/NguCityPage"));
const NguLocalHubPage = lazy(() => import("@/pages/NguLocalHubPage"));
const NguPageRegistryPage = lazy(() => import("@/pages/NguPageRegistryPage"));
// ComicStudio only loads in dev — prod gets a no-op stub to avoid bundling the editor

const ComicStudio = lazy((() =>
  import.meta.env.DEV ? import("@/pages/ComicStudio") : Promise.resolve({ default: () => null })
) as () => Promise<{ default: React.ComponentType<any> }>);

// DevOfferCopyHistory only loads in dev — git-history viewer for offer page copy
const DevOfferCopyHistory = lazy((() =>
  import.meta.env.DEV ? import("@/pages/DevOfferCopyHistory") : Promise.resolve({ default: () => null })
) as () => Promise<{ default: React.ComponentType<any> }>);

// NguVslPage only loads in dev — not yet promoted to production
const NguVslPage = lazy((() =>
  import.meta.env.DEV ? import("@/pages/NguVslPage") : Promise.resolve({ default: () => null })
) as () => Promise<{ default: React.ComponentType<any> }>);
// NguVslPageV2 — multi-artist studio owner accountability framing (production-eligible)
const NguVslPageV2 = lazy(() => import("@/pages/NguVslPageV2"));
const NguIcpVslPage = lazy(() => import("@/pages/NguIcpVslPage"));
const AppPage = lazy(() => import("@/pages/AppPage"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const CaseStudy = lazy(() => import("@/pages/CaseStudy"));
const ManageFlashDesigns = lazy(() => import("@/pages/ManageFlashDesigns"));
const ManageDiscountDrops = lazy(() => import("@/pages/ManageDiscountDrops"));
const ManageStudio = lazy(() => import("@/pages/ManageStudio"));
const ManageDesign = lazy(() => import("@/pages/ManageDesign"));
const AdminAddBooking = lazy(() => import("@/pages/AdminAddBooking"));
const AdminToday = lazy(() => import("@/pages/AdminToday"));
const AdminTodayChanges = lazy(() => import("@/pages/AdminTodayChanges"));
const AdminMessagesHub = lazy(() => import("@/pages/AdminMessagesHub"));
const AdminReviewsDashboard = lazy(() => import("@/pages/AdminReviewsDashboard"));
const AdminWalkinQueue = lazy(() => import("@/pages/AdminWalkinQueue"));
const AdminUpcomingBookings = lazy(() => import("@/pages/AdminUpcomingBookings"));
const AdminAllBookings = lazy(() => import("@/pages/AdminAllBookings"));
const AdminEmailSubscribers = lazy(() => import("@/pages/AdminEmailSubscribers"));
const AdminSupportTickets = lazy(() => import("@/pages/AdminSupportTickets"));
const AdminAgencyContracts = lazy(() => import("@/pages/AdminAgencyContracts"));
const AdminLegalTemplates = lazy(() => import("@/pages/AdminLegalTemplates"));
const AdminArtistDashboards = lazy(() => import("@/pages/AdminArtistDashboards"));
const AdminStudioCalendar = lazy(() => import("@/pages/AdminStudioCalendar"));
const AdminRecentlyCompleted = lazy(() => import("@/pages/AdminRecentlyCompleted"));
const AdminBookingAnalytics = lazy(() => import("@/pages/AdminBookingAnalytics"));
const AdminAppointmentTrends = lazy(() => import("@/pages/AdminAppointmentTrends"));
const AdminDraftRecovery = lazy(() => import("@/pages/AdminDraftRecovery"));
const AdminDeposits = lazy(() => import("@/pages/AdminDeposits"));
const ContactResponded = lazy(() => import("@/pages/ContactResponded"));
const ManageContacts = lazy(() => import("@/pages/ManageContacts"));
const AdminContactInbox = lazy(() => import("@/pages/AdminContactInbox"));
const AdminAuditLog = lazy(() => import("@/pages/AdminAuditLog"));
const AdminDailyActivity = lazy(() => import("@/pages/AdminDailyActivity"));
const ShopOwnerAuditLog = lazy(() => import("@/pages/ShopOwnerAuditLog"));
const AdminAnnouncementsLog = lazy(() => import("@/pages/AdminAnnouncementsLog"));
const AdminEmailLog = lazy(() => import("@/pages/AdminEmailLog"));
const CampaignAnalytics = lazy(() => import("@/pages/CampaignAnalytics"));
const CampaignHub = lazy(() => import("@/pages/CampaignHub"));
const AdminDeletedBookings = lazy(() => import("@/pages/AdminDeletedBookings"));
const ChangeLog = lazy(() => import("@/pages/ChangeLog"));
const EmailRoutingAudit = lazy(() => import("@/pages/EmailRoutingAudit"));
const AgencyEmailHub = lazy(() => import("@/pages/AgencyEmailHub"));
const AdminEmailGallery = lazy(() => import("@/pages/AdminEmailGallery"));
const EmptyStatesGallery = lazy(() => import("@/pages/EmptyStates"));
const OwnerEmailGallery = lazy(() => import("@/pages/OwnerEmailGallery"));
const ReleaseForm = lazy(() => import("@/pages/ReleaseForm"));
const AgencyContract = lazy(() => import("@/pages/AgencyContract"));
const CalendarFeeds = lazy(() => import("@/pages/CalendarFeeds"));
const FeatureFlags = lazy(() => import("@/pages/FeatureFlags"));
const DevFeatureFlagsRef = lazy(() => import("@/pages/DevFeatureFlagsRef"));
const SupportPortal = lazy(() => import("@/pages/SupportPortal"));
const SupportAdminView = lazy(() => import("@/pages/SupportAdminView"));
const CheckIn = lazy(() => import("@/pages/CheckIn"));
const KioskPage = lazy(() => import("@/pages/KioskPage"));
const WalkInRequest = lazy(() => import("@/pages/WalkInRequest"));
const AdminDocs = lazy(() => import("@/pages/AdminDocs"));
const NetGlowUpSalesHub = lazy(() => import("@/pages/NetGlowUpSalesHub"));
const SalesHubOverviewPage = lazy(() => import("@/pages/NetGlowUpSalesHub").then(m => ({ default: m.SalesHubOverviewPage })));
const SalesHubIcpProfilesPage = lazy(() => import("@/pages/NetGlowUpSalesHub").then(m => ({ default: m.SalesHubIcpProfilesPage })));
const SalesHubProspectingPage = lazy(() => import("@/pages/NetGlowUpSalesHub").then(m => ({ default: m.SalesHubProspectingPage })));
const SalesHubColdCallPage = lazy(() => import("@/pages/NetGlowUpSalesHub").then(m => ({ default: m.SalesHubColdCallPage })));
const SalesHubEmailPage = lazy(() => import("@/pages/NetGlowUpSalesHub").then(m => ({ default: m.SalesHubEmailPage })));
const SalesHubObjectionsPage = lazy(() => import("@/pages/NetGlowUpSalesHub").then(m => ({ default: m.SalesHubObjectionsPage })));
const SalesHubFaqPage = lazy(() => import("@/pages/NetGlowUpSalesHub").then(m => ({ default: m.SalesHubFaqPage })));
const SalesHubFeatureSheetsPage = lazy(() => import("@/pages/NetGlowUpSalesHub").then(m => ({ default: m.SalesHubFeatureSheetsPage })));
const SalesHubPricingPage = lazy(() => import("@/pages/NetGlowUpSalesHub").then(m => ({ default: m.SalesHubPricingPage })));
const SalesHubTechStackPage = lazy(() => import("@/pages/NetGlowUpSalesHub").then(m => ({ default: m.SalesHubTechStackPage })));
const SalesHubWhoNotHowPage = lazy(() => import("@/pages/NetGlowUpSalesHub").then(m => ({ default: m.SalesHubWhoNotHowPage })));
const SalesHubAdCreativePage = lazy(() => import("@/pages/NetGlowUpSalesHub").then(m => ({ default: m.SalesHubAdCreativePage })));
const SalesHubHookHeadlinePage = lazy(() => import("@/pages/NetGlowUpSalesHub").then(m => ({ default: m.SalesHubHookHeadlinePage })));
const SalesHubLeadIntakePage = lazy(() => import("@/pages/NetGlowUpSalesHub").then(m => ({ default: m.SalesHubLeadIntakePage })));
const SalesHubSprintPage = lazy(() => import("@/pages/SalesHubSprintPage").then(m => ({ default: m.SalesHubSprintPage })));
const SalesHubLapsPage = lazy(() => import("@/pages/SalesHubLapsPage").then(m => ({ default: m.SalesHubLapsPage })));
const SalesHubAiAutomationPage = lazy(() => import("@/pages/SalesHubAiAutomationPage"));
const SalesHubCanonicalOfferPage = lazy(() => import("@/pages/NetGlowUpSalesHub").then(m => ({ default: m.SalesHubCanonicalOfferPage })));
const NetGlowUpHubPrint = lazy(() => import("@/pages/NetGlowUpHubPrint"));
const NguExportDashboard = lazy(() => import("@/pages/NguExportDashboard"));
const NguContentCalendarPage = lazy(() => import("@/pages/NguContentCalendarPage"));
const NetGlowUpPePitch = lazy(() => import("@/pages/NetGlowUpPePitch"));
const AccountManagement = lazy(() => import("@/pages/AccountManagement"));
const AdminAccountInfo = lazy(() => import("@/pages/AdminAccountInfo"));
const DeploymentArchitecturePage = lazy(() => import("@/pages/DeploymentArchitecturePage"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const NguLeadsPipeline = lazy(() => import("@/pages/NguLeadsPipeline"));
const NguPipelineVelocity = lazy(() => import("@/pages/NguPipelineVelocity"));
const NguLeadAction = lazy(() => import("@/pages/NguLeadAction"));
const AgencyTracker = lazy(() => import("@/pages/AgencyTracker"));
const AdminLapsedClients = lazy(() => import("@/pages/AdminLapsedClients"));
const AdminArtistPerformance = lazy(() => import("@/pages/AdminArtistPerformance"));
const AdminCommissions = lazy(() => import("@/pages/AdminCommissions"));
const AdminCashFlow = lazy(() => import("@/pages/AdminCashFlow"));
const AdminExpenses = lazy(() => import("@/pages/AdminExpenses"));
const AdminClientLTV = lazy(() => import("@/pages/AdminClientLTV"));
const AdminROI = lazy(() => import("@/pages/AdminROI"));
const AdminAdCreatives = lazy(() => import("@/pages/AdminAdCreatives"));
const AdminRevenueGoals = lazy(() => import("@/pages/AdminRevenueGoals"));
const AdminPortalView = lazy(() => import("@/pages/AdminPortalView"));
const AdminClientPortalSessions = lazy(() => import("@/pages/AdminClientPortalSessions"));
const AdminClientPortalActivity = lazy(() => import("@/pages/AdminClientPortalActivity"));
const AdminVideoStudio = lazy(() => import("@/pages/AdminVideoStudio"));
const AdminWaitlist = lazy(() => import("@/pages/AdminWaitlist"));
const AdminTokenSecurity = lazy(() => import("@/pages/AdminTokenSecurity"));
const AdminStudioNews = lazy(() => import("@/pages/AdminStudioNews"));
const AdminStudioOwnerBriefing = lazy(() => import("@/pages/AdminStudioOwnerBriefing"));
const AdminSecretRotations = lazy(() => import("@/pages/AdminSecretRotations"));
const AdminUsage = lazy(() => import("@/pages/AdminUsage"));
const PredictionEngine = lazy(() => import("@/pages/PredictionEngine"));
const AdminDbBackups = lazy(() => import("@/pages/AdminDbBackups"));
const AdminDataExport = lazy(() => import("@/pages/AdminDataExport"));
const AdminOfferAudit = lazy(() => import("@/pages/AdminOfferAudit"));
const AdminSecurityIncident = lazy(() => import("@/pages/AdminSecurityIncident"));
const AdminStatusPage = lazy(() => import("@/pages/AdminStatusPage"));
const PublicStatusPage = lazy(() => import("@/pages/PublicStatusPage"));
const AdminOpsPage = lazy(() => import("@/pages/AdminOpsPage"));
const AdminPerfTrendPage = lazy(() => import("@/pages/AdminPerfTrendPage"));
const AdminExperimentsPage = lazy(() => import("@/pages/AdminExperimentsPage"));
const AdminRestoreDrills = lazy(() => import("@/pages/AdminRestoreDrills"));
const AdminJobs = lazy(() => import("@/pages/AdminJobs"));
const AdminSeoPage = lazy(() => import("@/pages/AdminSeoPage"));
const AdminControlsDashboard = lazy(() => import("@/pages/AdminControlsDashboard"));
const AdminPulse = lazy(() => import("@/pages/AdminPulse"));
const HealedPhotoSubmit = lazy(() => import("@/pages/HealedPhotoSubmit"));
const DepositReturn = lazy(() => import("@/pages/DepositReturn"));
const BalanceReturn = lazy(() => import("@/pages/BalanceReturn"));
const ClientPortalLogin = lazy(() => import("@/pages/ClientPortalLogin"));
const UnifiedLoginPage = lazy(() => import("@/pages/UnifiedLoginPage"));
const ClientPortalDashboard = lazy(() => import("@/pages/ClientPortalDashboard"));
const ClientPortalAppointment = lazy(() => import("@/pages/ClientPortalAppointment"));
const ClientPortalPortfolio = lazy(() => import("@/pages/ClientPortalPortfolio"));
const ClientPortalBodyMap = lazy(() => import("@/pages/ClientPortalBodyMap"));
const InspirationStudio = lazy(() => import("@/pages/InspirationStudio"));
const ClientPortalAIStudio = lazy(() => import("@/pages/ClientPortalAIStudio"));
const ClientPortalSizeCalibration = lazy(() => import("@/pages/ClientPortalSizeCalibration"));
const PublicPortfolio = lazy(() => import("@/pages/PublicPortfolio"));
const NetGlowUpVideoStudio = lazy(() => import("@/pages/NetGlowUpVideoStudio"));
const NetGlowUpVideoScriptsPage = lazy(() => import("@/pages/NetGlowUpVideoScriptsPage"));
const NetGlowUpVslGeneratorPage = lazy(() => import("@/pages/NetGlowUpVslGeneratorPage"));
const NetGlowUpFeatureVideosPage = lazy(() => import("@/pages/NetGlowUpFeatureVideosPage"));
const NetGlowUpValuationDashboard = lazy(() => import("@/pages/NetGlowUpValuationDashboard"));
const EmailPreferences = lazy(() => import("@/pages/EmailPreferences"));
const AgencyAdminDashboard = lazy(() => import("@/pages/AgencyAdminDashboard"));

/**
 * Creates a component that renders `Component` only when `flag` is enabled.
 * While flags are loading: renders null (shows Suspense fallback — no flash of
 * content before a potential redirect). Once loaded and flag is false: navigates
 * to "/" silently. All new ICPs control modules through this mechanism.
 */
function makeGuarded(flag: string, Component: React.ComponentType) {
  const Guarded = function () {
    const { isFlagEnabled, isLoaded } = useFeatureFlags();
    const [, navigate] = useLocation();
    const enabled = isFlagEnabled(flag);
    useEffect(() => {
      if (isLoaded && !enabled) navigate("/");
    }, [isLoaded, enabled, navigate]);
    if (!isLoaded || (isLoaded && !enabled)) return null;
    return <Component />;
  };
  Guarded.displayName = `Guarded(${flag})`;
  return Guarded;
}

const GuardedFlashDesigns  = makeGuarded("flash_gallery_enabled",  FlashDesigns);
const GuardedStyleQuiz     = makeGuarded("style_quiz_enabled",     StyleQuiz);
const GuardedEvents        = makeGuarded("events_page_enabled",    Events);
const GuardedHealingTracker = makeGuarded("healing_tracker_enabled", HealingTracker);

/**
 * Creates a component that renders `Component` only when the active APP_PROFILE
 * is NOT "agency". When APP_PROFILE=agency the user is redirected to "/" —
 * studio-specific features (booking, gallery, artist pages, etc.) are not part
 * of the agency deployment experience.
 *
 * Exported so tests can wrap a stub component with the real guard logic and
 * verify the redirect behaviour without an inline replica.
 */
export function makeProfileGuarded(Component: React.ComponentType) {
  const Guarded = function () {
    const { config, isLoading } = useStudioConfig();
    const [, navigate] = useLocation();
    const isAgency = config.profile === "agency";
    useEffect(() => {
      if (!isLoading && isAgency) navigate("/");
    }, [isLoading, isAgency, navigate]);
    if (isLoading || isAgency) return null;
    return <Component />;
  };
  Guarded.displayName = `ProfileGuarded(${Component.displayName ?? Component.name ?? "Component"})`;
  return Guarded;
}

// Studio-specific routes hidden in the agency profile.
// The feature-flag guards (GuardedEvents etc.) already redirect when flags are
// off; these wrappers add a hard deployment-level gate on top so the routes
// are unreachable even before DB flag data is fetched.
const ProfileGuardedBooking       = makeProfileGuarded(BookingCalendar as any);
const ProfileGuardedGallery       = makeProfileGuarded(Gallery);
const ProfileGuardedShop          = makeProfileGuarded(Shop);
// Artist page uses useParams so we need a thin wrapper.
const ProfileGuardedArtist        = makeProfileGuarded(Artist as any);
const ProfileGuardedFlashDesigns  = makeProfileGuarded(GuardedFlashDesigns);
const ProfileGuardedStyleQuiz     = makeProfileGuarded(GuardedStyleQuiz);
const ProfileGuardedEvents        = makeProfileGuarded(GuardedEvents);
const ProfileGuardedHealingTracker = makeProfileGuarded(GuardedHealingTracker);

// PLG self-serve signup — agency dev only, off by default in all customer deployments.
// Enable with feature flag "plg_self_serve_signup" via /admin/feature-flags when ready to test.
const GuardedNetGlowUpStudioSignup  = makeGuarded("plg_self_serve_signup", NetGlowUpStudioSignup);
const GuardedNetGlowUpSignupSuccess = makeGuarded("plg_self_serve_signup", NetGlowUpSignupSuccess);

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  return null;
}

/** Renders AgencyHome when APP_PROFILE=agency, otherwise the standard studio Home. */
export function HomeRouter() {
  const { config, isLoading } = useStudioConfig();
  if (isLoading) return null;
  if (config.profile === "agency") return <AgencyHome />;
  return <Home />;
}

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: "hsl(0 0% 5%)" }}>
      <div className="w-14 h-14 rounded-full bg-black/80 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
      </div>
    </div>
  );
}

const ADMIN_BANNER_KEY = "admin-banner-dismissed";

const BOTTOM_BANNER_VAR = "--bottom-banner-h";
const BOTTOM_BANNER_H = "48px";

function AdminBanner() {
  const [location] = useLocation();
  const [dismissed, setDismissed] = useState(() =>
    sessionStorage.getItem(ADMIN_BANNER_KEY) === "1"
  );

  const isAdminPage =
    location.startsWith("/admin/") || location.startsWith("/walkin/");

  const { openCount } = useModalContext();
  // Token is stripped from the URL via window.history.replaceState after
  // validation on every admin page (enforced by STEP-67 admin-token-strip-audit).
  // Banner is disabled because the URL bar no longer exposes the token.
  // To re-enable: remove `&& false` below and ensure URL-stripping is reverted.
  const visible = isAdminPage && !dismissed && openCount === 0 && false; // eslint-disable-line @typescript-eslint/no-unnecessary-condition

  useEffect(() => {
    document.documentElement.style.setProperty(BOTTOM_BANNER_VAR, (isAdminPage && !dismissed) ? BOTTOM_BANNER_H : "0px");
    return () => { document.documentElement.style.setProperty(BOTTOM_BANNER_VAR, "0px"); };
  }, [isAdminPage, dismissed]);

  if (!visible) return null;

  function dismiss() {
    sessionStorage.setItem(ADMIN_BANNER_KEY, "1");
    setDismissed(true);
  }

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9999] flex items-center gap-3 px-4 py-2.5 pwa-footer-hide"
      style={{ background: "linear-gradient(90deg, #78350f 0%, #92400e 100%)", borderTop: "1px solid #b45309" }}
      data-testid="admin-banner"
    >
      <span style={{ fontSize: 15 }}>🔒</span>
      <p className="flex-1 text-xs sm:text-sm text-amber-100 font-medium leading-snug">
        Hey, keep this link private!{" "}
        <span className="opacity-80">It gives access to your shop settings — don't share it or leave it open on shared devices.</span>
      </p>
      <button
        onClick={dismiss}
        aria-label="Dismiss admin warning"
        className="shrink-0 text-amber-300 hover:text-white text-xs underline underline-offset-2 transition-colors"
        data-testid="button-dismiss-admin-banner"
      >
        Got it
      </button>
    </div>
  );
}

export function GlobalTrainingBanner() {
  const [location] = useLocation();

  if (
    !location.startsWith("/admin/") ||
    location.startsWith("/admin/netglowup") ||
    location.startsWith("/admin/ngu-") ||
    location.startsWith("/admin/sales") ||
    location.startsWith("/admin/account")
  ) {
    return null;
  }

  const segments = location.split("/").filter(Boolean);
  // segments: ["admin", pageOrSub, tokenOrId, ...]

  let token: string | undefined;

  if (segments[1] === "security" && segments[2] === "incident") {
    // /admin/security/incident/:token/:tokenHash (full: length=5) or
    // /admin/security/incident/:tokenHash (post-strip: length=4 — tokenHash only)
    token = segments.length === 5 ? segments[3] : (typeof window !== "undefined" ? localStorage.getItem("ngu_admin_token") ?? "" : "");
  } else if (segments[1] === "campaigns") {
    // /admin/campaigns/:id/:token (full: length=4) or
    // /admin/campaigns/:id (post-strip: length=3)
    token = segments.length === 4 ? segments[3] : (typeof window !== "undefined" ? localStorage.getItem("ngu_admin_token") ?? "" : "");
  } else {
    // Standard: /admin/:page/:token (optionally followed by sub-pages).
    // On tokenless refreshes after strip, segments[2] is absent — fall back
    // to the most-recently-validated token stored in localStorage.
    token = segments[2] || (typeof window !== "undefined" ? localStorage.getItem("ngu_admin_token") ?? "" : "");
  }

  if (!token) return null;

  return <StudioTrainingBanner token={token} />;
}

interface SystemFlags {
  isAgencyDeployment: boolean;
  agencySiteUrl: string | null;
  nguClientRedirectEnabled: boolean;
}

const _systemFlagsCache: { value?: SystemFlags; error?: true } = {};

function useSystemFlags() {
  const [flags, setFlags] = useState<SystemFlags | "error" | null>(() => {
    if (_systemFlagsCache.value) return _systemFlagsCache.value;
    if (_systemFlagsCache.error) return "error";
    return null;
  });
  useEffect(() => {
    if (_systemFlagsCache.value || _systemFlagsCache.error) return;
    apiFetch(systemFlagsContract)
      .then((f) => {
        const normalized: SystemFlags = {
          isAgencyDeployment: f.isAgencyDeployment,
          agencySiteUrl: f.agencySiteUrl ?? null,
          nguClientRedirectEnabled: f.nguClientRedirectEnabled ?? false,
        };
        _systemFlagsCache.value = normalized;
        setFlags(normalized);
      })
      .catch(() => {
        _systemFlagsCache.error = true;
        setFlags("error");
      });
  }, []);
  return flags;
}

const NGU_SLUG_MAP: Record<string, string> = {
  "studio-owner": "studio_owner",
  "solo-artist":  "solo_artist",
  "agency":       "agency",
  "convention":   "convention",
  "educator":     "educator",
  "distributor":  "distributor",
  "pmu":          "adjacent",
  "adjacent":     "adjacent",
  "general":      "general",
};

function NguPublicRouter({ location }: { location: string }) {
  if (location === "/netglowup") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-home">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUp />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  const nguSegmentSlug = location.startsWith("/netglowup/")
    ? location.slice("/netglowup/".length)
    : null;
  const nguSegmentKey = nguSegmentSlug && !nguSegmentSlug.startsWith("profile/")
    ? NGU_SLUG_MAP[nguSegmentSlug] ?? null
    : null;

  if (nguSegmentKey) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-segment">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUp initialSegment={nguSegmentKey as any} />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/calculator" || location.startsWith("/netglowup/calculator/")) {
    const pathToken = location.startsWith("/netglowup/calculator/")
      ? location.slice("/netglowup/calculator/".length) || undefined
      : undefined;
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-calculator">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpCalculatorPage pathToken={pathToken} />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (import.meta.env.DEV && location === "/netglowup/pricing") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpPricingTiersPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/signup" || location.startsWith("/netglowup/signup?")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-signup">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <GuardedNetGlowUpStudioSignup />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/netglowup/signup/success")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-signup-success">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <GuardedNetGlowUpSignupSuccess />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/checkout/agency") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-checkout-agency">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpCheckoutAgency />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/checkout" || location.startsWith("/netglowup/checkout/success")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-checkout">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpCheckout />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/report") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-report">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpReport />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/demo") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-demo">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpDemo />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/stack") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-stack">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpStack />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/cost") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-cost">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpCostPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/no-show-calculator/get") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-magnet-no-show-calculator">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguMagnetLandingPage slug="no-show-calculator" />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/studio-audit/get") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-magnet-studio-audit">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguMagnetLandingPage slug="studio-audit" />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/reminder-templates/get") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-magnet-reminder-templates">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguMagnetLandingPage slug="reminder-templates" />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/stack-teardown/get") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-magnet-stack-teardown">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguMagnetLandingPage slug="stack-teardown" />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/30-day-roadmap/get") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-magnet-30-day-roadmap">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguMagnetLandingPage slug="30-day-roadmap" />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/no-show-calculator") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-no-show-calculator">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguNoShowCalculatorPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  // /netglowup/:segment/vsl — legacy redirect to /watch
  if (/^\/netglowup\/[^/]+\/vsl$/.test(location)) {
    const segment = location.split("/")[2];
    return <Redirect to={`/netglowup/${segment}/watch`} />;
  }

  // /netglowup/:segment/watch — platform walkthrough VSL
  // Matches /netglowup/studio-owner/watch, /netglowup/solo-artist/watch, etc.
  if (/^\/netglowup\/[^/]+\/watch$/.test(location)) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NguVslPageV2 />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/studio-audit") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-studio-audit">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguStudioAuditPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/reminder-templates") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-reminder-templates">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguReminderTemplatesPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/stack-teardown") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-stack-teardown">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguStackTeardownPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/30-day-roadmap") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-30-day-roadmap">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Ngu30DayRoadmapPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/local") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-local-hub">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguLocalHubPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/netglowup/local/")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-city-page">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguCityPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/dm-audit") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-dm-audit">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguDmAuditPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/artist-onboarding") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-artist-onboarding">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguArtistOnboardingPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/gbp-guide") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-gbp-guide">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguGbpGuidePage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/social-starter") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-social-starter">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguSocialStarterPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/email-list-guide") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-email-list-guide">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguEmailListGuidePage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/promo-calendar") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-promo-calendar">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguPromoCalendarPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/tattoo-photo-guide") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-tattoo-photo-guide">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguTattooPhotoGuidePage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/review-playbook") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-review-playbook">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguReviewPlaybookPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/aftercare-card") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-aftercare-card">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguAftercareCardPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/touch-up-policy") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-touch-up-policy">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguTouchUpPolicyPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/caption-guide") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-caption-guide">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguCaptionGuidePage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/get-featured") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-get-featured">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguGetFeaturedPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/style-guide") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-style-guide">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguStyleGuidePage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/flash-day-playbook/print") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <Suspense fallback={<PageLoader />}>
          <NguFlashDayChecklistPrintPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/flash-day-playbook") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-flash-day-playbook">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguFlashDayPlaybookPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/consultation-script") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-consultation-script">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguConsultationScriptPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/commission-guide") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-commission-guide">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguCommissionGuidePage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/portfolio-curation") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-portfolio-curation">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguPortfolioCurationPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/pricing-guide") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-pricing-guide">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguPricingGuidePage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/deposit-policy") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-deposit-policy">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguDepositPolicyPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/pages") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-page-registry">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguPageRegistryPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/features/videos") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-features-videos">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpFeatureDemoVideosPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/features") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-features">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpFeaturesPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/features/booking") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-features-booking">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpFeaturesBookingPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/features/accounting") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-features-accounting">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpFeaturesAccountingPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/features/commissions") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-features-commissions">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpFeaturesCommissionsPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/features/forecasting") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-features-forecasting">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpFeaturesForecastingPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/features/automations") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-features-automations">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpFeaturesAutomationsPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/features/analytics") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-features-analytics">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpFeaturesAnalyticsPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/features/client-management") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-features-client-management">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpFeaturesClientManagementPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/offer") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-offer">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpOfferPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/studios") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-studios">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpStudiosPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/why-email") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-why-email">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpWhyEmail />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/compliance") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-compliance">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpCompliancePage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/intake") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-intake">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpIntakePage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/book") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-book">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguBookPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/netglowup/survey/")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-survey">
          <ClarityProvider />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpPainSurveyPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (
    location === "/netglowup/comic" ||
    location.startsWith("/netglowup/comic/")
  ) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-comic">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpComic />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/webinar") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-webinar">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpWebinar />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location === "/netglowup/webinar/host") {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-webinar-host">
          <ClarityProvider />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpWebinarHost />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/netglowup/profile/")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-profile">
          {/* ClarityProvider intentionally omitted: the URL path contains a
              reusable bearer token that would be recorded by Clarity session
              replay / heatmaps, constituting third-party credential disclosure. */}
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NetGlowUpProfile />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/netglowup-comic")) {
    window.location.replace("/netglowup");
    return <PageLoader />;
  }

  // Unknown /netglowup* slug — show 404 rather than a blank page
  return <NotFound />;
}

export function Router() {
  useAnalytics();
  const [location, navigate] = useLocation();
  const systemFlags = useSystemFlags();
  const { isFlagEnabled } = useFeatureFlags();

  const isNguPublicRoute =
    location.startsWith("/netglowup") || location.startsWith("/netglowup-comic");

  if (isNguPublicRoute) {
    const flags = systemFlags === null || systemFlags === "error" ? null : systemFlags;
    if (flags?.nguClientRedirectEnabled) {
      const agencyBase = flags.agencySiteUrl ?? "https://netglowup.com";
      window.location.replace(`${agencyBase}${location}`);
      return <PageLoader />;
    }
    return (
      <PublicPageErrorBoundary route={location} context="ngu-public">
        <NguPublicRouter location={location} />
      </PublicPageErrorBoundary>
    );
  }

  // /overview is the standalone studio pitch deck — served as a separate HTML
  // file by the server (artifacts/studio-pitch-deck/index.html). When reached
  // via client-side SPA navigation (PrefetchLink / wouter pushState) the server
  // never sees the request, so we force a full-page reload to let the server
  // serve the correct standalone file.
  if (location === "/overview") {
    window.location.replace("/overview");
    return <PageLoader />;
  }

  if (location.startsWith("/ngcity/")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <PublicPageErrorBoundary route={location} context="ngu-city-page">
          <ClarityProvider /> {/* @clarity-ok: /ngcity/ prospect-facing NGU route */}
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <NguCityPage />
          </Suspense>
        </PublicPageErrorBoundary>
      </NguThemeProvider>
    );
  }

  // ── Agency deployment hard whitelist ────────────────────────────────────────
  // The agency site is a pure B2B marketing surface. No booking flows, no
  // client portal, no artist/receptionist portals, no admin dashboard.
  // Every route NOT on this list returns 404 — the inverse of the individual
  // ProfileGuarded pattern, which is additive and misses anything we forget.
  //
  // Routes already handled above (NGU funnel, ngcity) are implicitly allowed.
  // /support/* and /admin/netglowup/* remain reachable for the internal team.
  //
  if (IS_AGENCY_BUILD) {
    const agencyAllowed =
      location === "/" ||
      location.startsWith("/privacy") ||
      location.startsWith("/terms") ||
      location.startsWith("/accessibility") ||
      location === "/status" ||
      location.startsWith("/email-preferences") ||
      location.startsWith("/contract/") ||
      location === "/contact" ||
      location.startsWith("/admin/netglowup/");
    if (!agencyAllowed) {
      return (
        <PublicPageErrorBoundary route={location} context="agency-404">
          <Layout>
            <NotFound />
          </Layout>
        </PublicPageErrorBoundary>
      );
    }
  }

  if (location.startsWith("/support/admin")) { // agency-allowlist-ok: support portal intentionally excluded from agency build
    return (
      <PublicPageErrorBoundary route={location} context="support-admin">
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <SupportAdminView />
        </Suspense>
      </PublicPageErrorBoundary>
    );
  }

  if (location.startsWith("/support/") || location === "/support") { // agency-allowlist-ok: support portal intentionally excluded from agency build
    return (
      <PublicPageErrorBoundary route={location} context="support">
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <SupportPortal />
        </Suspense>
      </PublicPageErrorBoundary>
    );
  }

  if (location.startsWith("/admin/netglowup/marketing-dashboard")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpMarketingDashboard />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/ads-integration")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpAdsIntegrationPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/ads")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpAdsPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/funnel")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpFunnelPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (import.meta.env.DEV && location === "/dev/comic-studio") {
    return (
      <>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <ComicStudio />
        </Suspense>
      </>
    );
  }

  if (import.meta.env.DEV && location === "/dev/offer-copy-history") {
    return (
      <Suspense fallback={<PageLoader />}>
        <DevOfferCopyHistory />
      </Suspense>
    );
  }

  if (import.meta.env.DEV && location === "/dev/ngu-vsl") {
    return (
      <Suspense fallback={<PageLoader />}>
        <NguVslPage />
      </Suspense>
    );
  }

  if (import.meta.env.DEV && location === "/dev/flags") {
    return (
      <Suspense fallback={<PageLoader />}>
        <DevFeatureFlagsRef />
      </Suspense>
    );
  }

  if (location === "/ngu-vsl-v2") { // agency-allowlist-ok: legacy VSL path, /netglowup/:segment/watch is the agency-facing path
    return (
      <Suspense fallback={<PageLoader />}>
        <NguVslPageV2 />
      </Suspense>
    );
  }

  if (location.startsWith("/admin/netglowup/1pmp")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpMarketingPlanPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/24-assets")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUp24AssetsPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/100m-offers")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpHormoziOffersPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/100m-leads")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpHormoziLeadsPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/100m-money-models")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpHormoziMoneyModelsPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/content-hub")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpContentHubPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/software-as-a-science")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpSoftwareAsSciencePage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/marketing-channels")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpMarketingChannelsPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/competitor-intel")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpCompetitorIntelPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/icp-registry")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpIcpRegistryPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/retention")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpRetentionPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/pain-survey")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpPainSurveyResultsPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/artist-survey")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpArtistSurveyResultsPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/onboarding")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <AdminOnboardingPanel />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/pulse")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Route path="/admin/netglowup/pulse/:token?" component={AdminPulse} />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/survey")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpSurveyResults />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/video-scripts")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpVideoScriptsPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/vsl-generator")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpVslGeneratorPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/feature-videos")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpFeatureVideosPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/video")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpVideoStudio />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/hub/print/")) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Route path="/admin/netglowup/hub/print/:token?" component={NetGlowUpHubPrint} />
      </Suspense>
    );
  }

  if (location.startsWith("/admin/netglowup/content-calendar")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <Suspense fallback={<PageLoader />}>
          <Route path="/admin/netglowup/content-calendar/:token?" component={NguContentCalendarPage} />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/export")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <Suspense fallback={<PageLoader />}>
          <Route path="/admin/netglowup/export/:token?" component={NguExportDashboard} />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.match(/\/admin\/netglowup\/hub\/[^/?#]+\/campaign-setup/)) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpHubCampaignSetup />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.match(/\/admin\/netglowup\/hub\/[^/?#]+\/sales-tokens/)) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpHubSalesTokens />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.match(/\/admin\/netglowup\/hub\/[^/?#]+\/analytics/)) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpHubAnalytics />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.match(/\/admin\/netglowup\/hub\/[^/?#]+\/drip-sequence/)) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpHubDripSequence />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/lead-magnets")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NguLeadMagnetHubPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/pe-pitch")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <Suspense fallback={<PageLoader />}>
          <Route path="/admin/netglowup/pe-pitch/:token?"><NetGlowUpPePitch /></Route>
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/hub")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpHubPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/marketing-chat")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpMarketingChat />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/ops")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NgOpsPortalPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/studios/new")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NgNewStudioWizard />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/performance")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <Suspense fallback={<div />}>
          <NgStudioPerformancePage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/studios")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NgStudiosPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/config")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpConfigPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/cost")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpSalesCoachPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/playbook-outsource")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpPlaybookOutsourcePage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/playbook")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpPlaybookPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/outreach")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpOutreachPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/prospects")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NguProspectListPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/qla")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpQLAPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/agencies")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpAgenciesPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/comparison")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpComparisonPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/usage")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpClientUsagePage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/signups")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Route path="/admin/netglowup/signups/:token" component={NguSignupsDashboard} />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location.startsWith("/admin/netglowup/report-card")) {
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <Route path="/admin/netglowup/report-card/:token" component={AdminNetGlowUpReportCard} />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if (location === "/admin/netglowup" || location.startsWith("/admin/netglowup/")) { // agency-allowlist-ok: /admin/netglowup/ prefix covered; bare /admin/netglowup only reachable via token redirect
    // Redirect bare /admin/netglowup/:token (single path segment) to the unified agency dashboard.
    // All sub-page slugs (funnel, hub, playbook, etc.) are caught by earlier guards above.
    const singleSeg = location.match(/^\/admin\/netglowup\/([^/]+)$/);
    if (singleSeg) {
      const KNOWN_NGU_SLUGS = ["cost", "playbook-outsource", "playbook", "outreach", "qla",
        "agencies", "comparison", "usage", "survey", "pain-survey"];
      const seg = singleSeg[1];
      if (!KNOWN_NGU_SLUGS.some(s => seg.startsWith(s))) {
        return <NguAdminRedirect token={seg} />;
      }
    }
    return (
      <NguThemeProvider>
        <NguThemeToggle />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <NetGlowUpAdminPage />
        </Suspense>
      </NguThemeProvider>
    );
  }

  if ((location === "/embed" || location.startsWith("/embed?")) && isFlagEnabled("booking_embed_enabled")) { // agency-allowlist-ok: booking embed is a studio feature, not shown on agency build
    return (
      <PublicPageErrorBoundary route={location} context="embed">
        <Suspense fallback={<PageLoader />}>
          <Route path="/embed" component={EmbedBooking} />
        </Suspense>
      </PublicPageErrorBoundary>
    );
  }

  if (location.startsWith("/messages/") || /^\/admin\/action\/[^/]+\/chat$/.test(location)) { // agency-allowlist-ok: booking messages are a studio feature, not shown on agency build
    return (
      <PublicPageErrorBoundary route={location} context="messages">
        <Suspense fallback={<PageLoader />}>
          <Switch>
            <Route path="/messages/:token" component={BookingMessages} />
            {/* token-strip-exception: manage token (not admin key) — different token type handled by AdminAction/AdminChat */}
            <Route path="/admin/action/:token/chat" component={AdminChat} />
          </Switch>
        </Suspense>
      </PublicPageErrorBoundary>
    );
  }

  if (location.startsWith("/email-preferences")) {
    return (
      <PublicPageErrorBoundary route={location} context="email-preferences">
        <Suspense fallback={<PageLoader />}>
          <Route path="/email-preferences" component={EmailPreferences} />
        </Suspense>
      </PublicPageErrorBoundary>
    );
  }

  // The main-layout boundary intentionally covers all remaining routes,
  // including tokenized admin/artist/portal pages that fall through here.
  // This prevents white-screen crashes across every surface and is consistent
  // with the task goal of "no blank screens on errors for public-facing pages."
  // Tokenized paths are safe because route is sanitized via sanitizeUrlForLogging
  // before being forwarded to Sentry.
  return (
    <PublicPageErrorBoundary route={location} context="main-layout">
      <Layout>
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/" component={HomeRouter} />
          <Route path="/artist/:id" component={ProfileGuardedArtist} />
          <Route path="/events" component={ProfileGuardedEvents} />
          <Route path="/about" component={About} />
          <Route path="/flash" component={ProfileGuardedFlashDesigns} />
          <Route path="/shop" component={ProfileGuardedShop} />
          <Route path="/privacy" component={Privacy} />
          <Route path="/terms" component={Terms} />
          <Route path="/accessibility" component={Accessibility} />
          <Route path="/contact" component={Contact} />
          <Route path="/healing" component={ProfileGuardedHealingTracker} />
          <Route path="/aftercare" component={Aftercare} />
          <Route path="/aftercare/piercing" component={PiercingAftercare} />
          <Route path="/numbing-cream-info" component={NumbingCreamInfo} />
          <Route path="/quiz" component={ProfileGuardedStyleQuiz} />
          <Route path="/mailing-list" component={MailingList} />
          <Route path="/booking" component={ProfileGuardedBooking} />
          <Route path="/estimate" component={CostEstimator} />
          <Route path="/gallery" component={ProfileGuardedGallery} />
          <Route path="/reschedule/:token" component={RescheduleBooking} />
          <Route path="/waitlist/claim/:token" component={WaitlistClaim} />
          <Route path="/booking/edit-details/:token" component={EditTattooDetails} />
          <Route path="/nps/:bookingId/:token" component={NpsRating} />
          <Route path="/messages/:token" component={BookingMessages} />
          <Route path="/design-review/:token" component={DesignReview} />
          {/* token-strip-exception: manage token (not admin key) — AdminAction/AdminChat use a different scoped manage token, not the admin key that useAdminPageToken manages */}
          <Route path="/admin/action/:token/chat" component={AdminChat} />
          {/* /:token? (optional) is intentional — prevents AdminAction from unmounting when
              window.history.replaceState strips the token from the URL. Wouter v3 monkey-patches
              replaceState and fires a synthetic location event; without the optional token the
              route stops matching /admin/action (no token segment) and React unmounts the component
              before the booking query can resolve. Making the token optional keeps AdminAction
              at the same tree position so the frozen useState token survives the strip. */}
          <Route path="/admin/action/:token?" component={AdminAction} />
          {/* Tokenless sibling so the router does not unmount ArtistPortal when the URL
              is stripped by window.history.replaceState after token validation. */}
          <Route path="/artist-portal" component={ArtistPortal} />
          <Route path="/artist-portal/:token" component={ArtistPortal} />
          <Route path="/admin/availability/:token?" component={ManageAvailability} />
          <Route path="/walkin/:token" component={WalkinBooking} />
          <Route path="/checkin/:token" component={CheckIn} />
          <Route path="/kiosk/:studioSlug" component={KioskPage} />
          <Route path="/walk-in-request" component={WalkInRequest} />
          <Route path="/admin/artists/:token?" component={ManageArtists} />
          <Route path="/admin/staff/:token?" component={ManageStaff} />
          {/* Tokenless setup landing — used by the staff-portal-redirect OTC
              fallback when no receptionist exists. ManageStaff falls back to
              the admin token in localStorage. Keeps the raw admin credential
              out of the URL (INC-2026-04-25-admin-token-url-disclosure). */}
          <Route path="/admin/staff" component={ManageStaff} />
          {/* Tokenless sibling so the router does not unmount ReceptionistPortal when
              the URL is reloaded without a token (unlikely but keeps the router stable). */}
          <Route path="/receptionist-portal" component={ReceptionistPortal} />
          <Route path="/receptionist-portal/:token" component={ReceptionistPortal} />
          <Route path="/receptionist-onboarding/:token" component={ReceptionistOnboardingRedirect} />
          {/* Agency Assistant Portal — agency-scoped token, no studio operations */}
          <Route path="/agency-portal" component={AgencyAssistantPortal} />
          <Route path="/agency-portal/:token" component={AgencyAssistantPortal} />
          <Route path="/agency-portal/onboarding" component={AgencyAssistantOnboarding} />
          {/* Studio onboarding survey — public, token-gated */}
          <Route path="/studio-survey/:token" component={() => (
            <Suspense fallback={<PageLoader />}>
              <OnboardingSurvey />
            </Suspense>
          )} />
          <Route path="/onboarding/:token" component={() => (
            <Suspense fallback={<PageLoader />}>
              <OnboardingSurvey />
            </Suspense>
          )} />
          <Route path="/admin/agency-tokens/:token?" component={AgencyPortalTokens} />
          <Route path="/admin/events/:token?" component={ManageEvents} />
          <Route path="/admin/email-settings/:token?" component={EmailSettings} />
          <Route path="/admin/estimate-model/:token?" component={EstimateModel} />
          <Route path="/admin/onboarding/:token?" component={Onboarding} />
          <Route path="/admin/case-study/:token?" component={CaseStudy} />
          <Route path="/admin/flash/:token?" component={ManageFlashDesigns} />
          <Route path="/admin/discount-drops/:token?" component={ManageDiscountDrops} />
          <Route path="/admin/studio/:token?" component={ManageStudio} />
          <Route path="/admin/design/:token?" component={ManageDesign} />
          <Route path="/admin/add-booking/:token?" component={AdminAddBooking} />
          <Route path="/admin/today/:token?" component={AdminToday} />
          <Route path="/admin/today-changes/:token?" component={AdminTodayChanges} />
          <Route path="/admin/messages/:token?" component={AdminMessagesHub} />
          <Route path="/admin/reviews/:token?" component={AdminReviewsDashboard} />
          <Route path="/admin/walkin-queue/:token?" component={AdminWalkinQueue} />
          <Route path="/admin/upcoming-bookings/:token?" component={AdminUpcomingBookings} />
          <Route path="/admin/action-items/:token?" component={lazy(() => import("@/pages/AdminActionItems"))} />
          <Route path="/admin/all-bookings/:token?" component={AdminAllBookings} />
          <Route path="/admin/email-subscribers/:token?" component={AdminEmailSubscribers} />
          <Route path="/admin/support-tickets/:token?" component={AdminSupportTickets} />
          <Route path="/admin/agency-contracts/:token?" component={AdminAgencyContracts} />
          <Route path="/admin/legal-templates/:token?" component={AdminLegalTemplates} />
          <Route path="/admin/artist-dashboards/:token?" component={AdminArtistDashboards} />
          <Route path="/admin/studio-calendar/:token?" component={AdminStudioCalendar} />
          <Route path="/admin/recently-completed/:token?" component={AdminRecentlyCompleted} />
          <Route path="/admin/booking-analytics/:token?" component={AdminBookingAnalytics} />
          <Route path="/admin/appointment-trends/:token?" component={AdminAppointmentTrends} />
          <Route path="/admin/draft-recovery/:token?" component={AdminDraftRecovery} />
          <Route path="/admin/deposits/:token?" component={AdminDeposits} />
          <Route path="/deposit-return" component={DepositReturn} />
          <Route path="/balance-return" component={BalanceReturn} />
          <Route path="/admin/contact-responded" component={ContactResponded} />
          <Route path="/admin/contacts" component={ManageContacts} />
          <Route path="/admin/contacts/:token?" component={ManageContacts} />
          <Route path="/admin/contact-inbox/:token?" component={AdminContactInbox} />
          <Route path="/admin/audit-log/:token?" component={AdminAuditLog} />
          <Route path="/admin/daily-activity/:token?" component={AdminDailyActivity} />
          <Route path="/admin/activity-log/:token?" component={ShopOwnerAuditLog} />
          <Route path="/admin/studio-news/:token?" component={AdminStudioNews} />
          <Route path="/admin/studio-owner-briefing/:token?" component={AdminStudioOwnerBriefing} />
          <Route path="/admin/announcements/:token?" component={AdminAnnouncementsLog} />
          <Route path="/admin/email-log/:token?" component={AdminEmailLog} />
          <Route path="/admin/campaign-hub/:token?" component={CampaignHub} />
          <Route path="/admin/campaigns/:id/:token?" component={CampaignAnalytics} />
          <Route path="/admin/deleted-bookings/:token?" component={AdminDeletedBookings} />
          <Route path="/admin/changelog/:token?" component={ChangeLog} />
          <Route path="/admin/email-routing" component={EmailRoutingAudit} />
          <Route path="/admin/email-routing/:token?" component={EmailRoutingAudit} />
          <Route path="/admin/agency/:token?" component={AgencyAdminDashboard} />
          <Route path="/admin/agency-email/:token?" component={AgencyEmailHub} />
          <Route path="/admin/email-gallery" component={AdminEmailGallery} />
          <Route path="/admin/email-gallery/:token?" component={AdminEmailGallery} />
          <Route path="/admin/empty-states/:token?" component={EmptyStatesGallery} />
          <Route path="/admin/owner-email-gallery" component={OwnerEmailGallery} />
          <Route path="/admin/owner-email-gallery/:token?" component={OwnerEmailGallery} />
          <Route path="/admin/docs/:token?" component={AdminDocs} />
          {/* token-strip-exception: sales sub-pages — token is in the middle of the path (/admin/sales/:token/sub-page).
              SalesSubPageRedirect immediately navigates away; the token is never left lingering.
              Making :token optional would match /admin/sales//overview (double-slash) and break routing. */}
          <Route path="/admin/sales/:token/overview" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/icp-profiles" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/prospecting" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/cold-call" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/email" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/objections" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/faq" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/feature-sheets" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/pricing" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/tech-stack" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/who-not-how" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/ad-creative" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/hook-headline" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/sprint" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/laps" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/ai-automation" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/plg" component={SalesSubPageRedirect} />
          <Route path="/admin/sales/:token/canonical-offer" component={SalesSubPageRedirect} />
          <Route path="/admin/sales-hub/:token/lead-intake" component={SalesHubLeadIntakePage} />
          <Route path="/admin/sales/:token?" component={SalesHubRedirect} />
          <Route path="/admin/dashboard" component={AdminDashboard} />
          <Route path="/admin/account/:token?" component={AccountManagement} />
          <Route path="/admin/account-info/:token?" component={AdminAccountInfo} />
          <Route path="/admin/deployment-architecture/:token?" component={DeploymentArchRedirect} />
          <Route path="/admin/controls/:token?" component={AdminControlsDashboard} />
          <Route path="/admin/ngu-valuation/:token?" component={NguValuationRedirect} />
          {/* token-strip-exception: redirect-only component — NguVelocityRedirect immediately navigates away on mount; no persistent URL state */}
          <Route path="/admin/ngu-pipeline/velocity/:token" component={NguVelocityRedirect} />
          <Route path="/admin/ngu-pipeline/:token?" component={NguPipelineRedirect} />
          <Route path="/admin/ngu-lead-action/:leadId/:token?" component={NguLeadAction} />
          <Route path="/admin/ical/:token?" component={CalendarFeeds} />
          <Route path="/admin/feature-flags/:token?" component={FeatureFlags} />
          <Route path="/release/:token" component={ReleaseForm} />
          <Route path="/contract/:token" component={AgencyContract} />
          <Route path="/admin/portal-view/:token?" component={AdminPortalView} />
          <Route path="/admin/client-portal-sessions/:token?" component={AdminClientPortalSessions} />
          <Route path="/admin/client-portal-activity/:token?" component={AdminClientPortalActivity} />
          <Route path="/admin/video-studio/:token?" component={AdminVideoStudio} />
          <Route path="/admin/lapsed-clients/:token?" component={AdminLapsedClients} />
          <Route path="/admin/artist-performance/:token?" component={AdminArtistPerformance} />
          <Route path="/admin/commissions/:token?" component={AdminCommissions} />
          <Route path="/admin/cash-flow/:token?" component={AdminCashFlow} />
          <Route path="/admin/expenses/:token?" component={AdminExpenses} />
          <Route path="/admin/client-ltv/:token?" component={AdminClientLTV} />
          <Route path="/admin/roi/:token?" component={AdminROI} />
          <Route path="/admin/ad-creatives/:token?" component={AdminAdCreatives} />
          <Route path="/admin/revenue-goals/:token?" component={AdminRevenueGoals} />
          <Route path="/admin/waitlist/:token?" component={AdminWaitlist} />
          <Route path="/admin/piercing-pricing/:token?" component={PiercingPricingRedirect} />
          {/* Full-access route: /admin/security/incident/:token/:tokenHash — admin token stripped after validation; tokenHash kept in URL for API calls */}
          <Route path="/admin/security/incident/:token/:tokenHash" component={AdminSecurityIncident} />
          {/* Post-strip tokenless route: admin token recovered from localStorage; tokenHash kept in URL for API calls */}
          <Route path="/admin/security/incident/:tokenHash?" component={AdminSecurityIncident} />
          <Route path="/admin/security/:token?" component={AdminTokenSecurity} />
          <Route path="/admin/secrets/:token?" component={AdminSecretRotations} />
          <Route path="/admin/usage/:token?" component={AdminUsage} />
          <Route path="/admin/db-backups/:token?" component={AdminDbBackups} />
          <Route path="/admin/offer-audit/:token?" component={AdminOfferAudit} />
          <Route path="/admin/data-export/:token?" component={AdminDataExport} />
          <Route path="/status" component={PublicStatusPage} />
          <Route path="/admin/status/:token?" component={AdminStatusPage} />
          <Route path="/admin/ops/restore-drills/:token?" component={AdminRestoreDrills} />
          <Route path="/admin/ops/:token?" component={AdminOpsPage} />
          <Route path="/admin/experiments/:token?" component={AdminExperimentsPage} />
          <Route path="/admin/perf-trend/:token?" component={AdminPerfTrendPage} />
          <Route path="/admin/jobs/:token?" component={AdminJobs} />
          <Route path="/admin/seo/:token?" component={AdminSeoPage} />
          <Route path="/admin/prediction/:token?" component={PredictionEngine} />
          <Route path="/healed-photo/:bookingId/:token" component={HealedPhotoSubmit} />
          <Route path="/agency/:token" component={AgencyTrackerRedirect} />
          <Route path="/app" component={AppPage} />
          <Route path="/login" component={UnifiedLoginPage} />
          <Route path="/my" component={ClientPortalLogin} />
          <Route path="/my/verify/:token" component={ClientPortalLogin} />
          <Route path="/my/dashboard" component={ClientPortalDashboard} />
          <Route path="/my/appointment/:referenceCode" component={ClientPortalAppointment} />
          <Route path="/my/portfolio" component={ClientPortalPortfolio} />
          <Route path="/my/inspiration" component={InspirationStudio} />
          <Route path="/my/body-map" component={ClientPortalBodyMap} />
          <Route path="/my/ai-studio" component={ClientPortalAIStudio} />
          <Route path="/my/size-preview" component={ClientPortalSizeCalibration} />
          <Route path="/my/ar-preview" component={ClientPortalSizeCalibration} />
          <Route path="/portfolio/:shareToken" component={PublicPortfolio} />
          <Route path="/survey/owner/:token" component={(props: { token?: string }) => { window.location.replace(`/admin/action${props.token ? `#pulse` : ""}`); return null; }} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </Layout>
    </PublicPageErrorBoundary>
  );
}

// GA (analytics) — always init, no marketing consent required
function initGoogleAnalytics() {
  if (import.meta.env.VITE_GA_MEASUREMENT_ID) initGA();
}

// Meta Pixel (marketing) — only called when user accepts marketing cookies
function initMarketingCookies() {
  if (import.meta.env.VITE_META_PIXEL_ID) initMetaPixel();
}

async function initDynamicTracking(marketingConsented: boolean) {
  const envPixelId = import.meta.env.VITE_META_PIXEL_ID;
  try {
    // Use the shared cached module — avoids a duplicate uncached fetch on every page load
    const cfg = await getNguTrackingConfig();
    // GA is env-only (VITE_GA_MEASUREMENT_ID required). The DB gaMeasurementId
    // field is informational only — no DB-fallback init for GA.
    // Meta Pixel: only when marketing consent is granted
    if (marketingConsented && !envPixelId && cfg.metaPixelId) initMetaPixelWithId(cfg.metaPixelId);
  } catch {
    /* intentionally silent */
  }
}


function PiercingPricingRedirect() {
  const [path, navigate] = useLocation();
  useEffect(() => {
    const token = path.split("/admin/piercing-pricing/")[1] ?? "";
    navigate(`/admin/estimate-model/${token}?tab=piercing`, { replace: true });
  }, [path, navigate]);
  return null;
}

const SALES_SUB_MAP: Record<string, string> = {
  "overview":       "hub",
  "icp-profiles":   "icp",
  "prospecting":    "prospect",
  "cold-call":      "coldcall",
  "email":          "email",
  "objections":     "objections",
  "faq":            "faq",
  "feature-sheets": "fsheets",
  "pricing":        "pricing",
  "tech-stack":     "techstack",
  "who-not-how":    "whonotho",
  "ad-creative":    "adcreative",
  "hook-headline":  "hook",
  "sprint":         "sprint",
  "laps":           "laps",
  "ai-automation":  "ai",
  "lead-intake":    "lead",
  "plg":            "plg",
  "canonical-offer": "canonical-offer",
};

// Task #1190 — SalesSubPageRedirect fix (INC: ReferenceError: SalesSubPageRedirect is not defined)
// Defined as a plain module-level function (not lazy) so it is always in scope
// when Wouter renders the matching /admin/sales/:token/* routes above.
// Do NOT convert to a lazy import — the lazy wrapper was what triggered the
// production ReferenceError that appeared in Sentry on 2026-05-04.
function SalesSubPageRedirect() {
  const { token } = useParams<{ token: string }>();
  const [location, navigate] = useLocation();
  useEffect(() => {
    const parts = location.split("/");
    const slug = parts[parts.length - 1] ?? "";
    const sub = SALES_SUB_MAP[slug] ?? "hub";
    navigate(`/admin/agency/${token ?? ""}?tab=sales&sub=${sub}`, { replace: true });
  }, [token, location, navigate]);
  return null;
}

function SalesHubRedirect() {
  const { token } = useParams<{ token: string }>();
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate(`/admin/agency/${token ?? ""}?tab=sales`, { replace: true });
  }, [token, navigate]);
  return null;
}

function DeploymentArchRedirect() {
  const { token } = useParams<{ token: string }>();
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate(`/admin/agency/${token ?? ""}?tab=deployment`, { replace: true });
  }, [token, navigate]);
  return null;
}

function NguValuationRedirect() {
  const { token } = useParams<{ token: string }>();
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate(`/admin/agency/${token ?? ""}?tab=crm&sub=valuation`, { replace: true });
  }, [token, navigate]);
  return null;
}

function NguVelocityRedirect() {
  const { token } = useParams<{ token: string }>();
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate(`/admin/agency/${token ?? ""}?tab=crm&sub=velocity`, { replace: true });
  }, [token, navigate]);
  return null;
}

function NguPipelineRedirect() {
  const { token } = useParams<{ token: string }>();
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate(`/admin/agency/${token ?? ""}?tab=crm`, { replace: true });
  }, [token, navigate]);
  return null;
}

function AgencyTrackerRedirect() {
  const { token } = useParams<{ token: string }>();
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate(`/admin/agency/${token ?? ""}?tab=deployment&sub=tracker`, { replace: true });
  }, [token, navigate]);
  return null;
}

function NguAdminRedirect({ token }: { token: string }) {
  const [, navigate] = useLocation();
  useEffect(() => {
    navigate(`/admin/agency/${token}?tab=marketing`, { replace: true });
  }, [token, navigate]);
  return null;
}

function ReceptionistOnboardingRedirect() {
  const [path, navigate] = useLocation();
  useEffect(() => {
    const token = path.split("/receptionist-onboarding/")[1] ?? "";
    navigate(`/receptionist-portal/${token}`, { replace: true });
  }, [path, navigate]);
  return null;
}

function App() {
  useEffect(() => {
    // Sentry + Web Vitals: boot immediately so we capture errors and metrics
    // from the very first paint.
    if (import.meta.env.VITE_SENTRY_DSN) {
      initSentry()
        .then(() => import("@/lib/web-vitals"))
        .then((m) => m.initWebVitals())
        .catch(() => { /* non-fatal */ });
    } else {
      import("@/lib/web-vitals").then((m) => m.initWebVitals()).catch(() => { /* intentionally silent: best-effort side-effect */ });
    }

    // Global 401 interceptor — catches auth failures on admin/manage-token routes
    // so they appear in Sentry before anyone has to report them manually
    const _origFetch = window.fetch;
    window.fetch = async function patchedFetch(input, init) {
      const res = await _origFetch(input, init);
      if (res.status === 401) {
        const url = typeof input === "string" ? input : input instanceof URL ? input.href : (input as Request).url;
        const isProtected = /\/api\/admin\//i.test(url) || /\/api\/artist\//i.test(url);
        if (isProtected) {
          const hdrs = (init?.headers || {}) as Record<string, string>;
          const hasAdminKey = !!hdrs["x-admin-key"];
          const hasManageToken = !!hdrs["x-manage-token"];
          if (!hasAdminKey && !hasManageToken) {
            captureAuthFailure(url, { method: init?.method || "GET", hasAdminKey, hasManageToken });
          }
        }
      }
      return res;
    };

    registerServiceWorker();
    queryClient.prefetchQuery({ queryKey: ["artists"], queryFn: fetchArtistsFromApi });

    // If the user has an admin/manage token in localStorage, preload the
    // admin-pages chunk in the background so the first admin route loads
    // instantly (no network round-trip for admin-pages.js on click).
    try {
      const hasAdminToken =
        !!localStorage.getItem("adminKey") ||
        !!localStorage.getItem("ngu_admin_token");
      if (hasAdminToken) {
        const prefetchAdmin = () => {
          import("@/pages/AdminToday").catch(() => { /* best-effort prefetch */ });
        };
        if ("requestIdleCallback" in window) {
          (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(prefetchAdmin);
        } else {
          setTimeout(prefetchAdmin, 1500);
        }
      }
    } catch { /* localStorage unavailable — skip prefetch */ }

    // Defer analytics + attribution until after the page load event so they
    // don't block the main thread during initial parse and paint.
    const runAfterLoad = () => {
      const alreadyAccepted = getStoredConsent() === "accepted";
      // GA: always init, no marketing consent required
      initGoogleAnalytics();
      if (alreadyAccepted) initMarketingCookies();
      initDynamicTracking(alreadyAccepted);
      initAttribution();
    };

    if (document.readyState === "complete") {
      // Already loaded — schedule on next idle tick to stay off the critical path
      if ("requestIdleCallback" in window) {
        (window as Window & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(runAfterLoad);
      } else {
        setTimeout(runAfterLoad, 200);
      }
    } else {
      window.addEventListener("load", runAfterLoad, { once: true });
    }
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <SiteConfigProvider>
            <FeatureFlagProvider>
              <ModalProvider>
                <Toaster />
                <BannerSequence
                  onAcceptCookies={() => { initMarketingCookies(); initDynamicTracking(true); }}
                  onDeclineCookies={() => {}}
                />
                <AdminBanner />
                <GlobalTrainingBanner />
                <AdminColorSchemeWidget />
                <Suspense fallback={null}><AdminCommandPalette /></Suspense>
                <Suspense fallback={null}><DevFlagPanel /></Suspense>
                <Router />
              </ModalProvider>
            </FeatureFlagProvider>
          </SiteConfigProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
